/**
 * RichTextEditor — editor de texto enriquecido (contentEditable) con barra de
 * herramientas: negrita, cursiva, subrayado, enlace, listas. Devuelve HTML.
 * Se usa para redactar mensajes a los viajeros (modo Foco de Tareas y ficha del
 * lead) para poder poner negritas e hipervínculos (p.ej. link de YouCanBookMe).
 *
 * Uso: ref imperativo (setText/setHTML/getHTML/getText/clear/focus) + onChange.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Bold, Italic, Underline, Link as LinkIcon, List, ListOrdered } from "lucide-react";

const escapeHtml = (s = "") =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const textToHtml = (text = "") =>
  (text || "")
    .split("\n")
    .map((line) => (line.trim() === "" ? "<p><br></p>" : `<p>${escapeHtml(line)}</p>`))
    .join("") || "";

function ToolbarButton({ title, onClick, children, testid }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      {children}
    </button>
  );
}

export const RichTextEditor = forwardRef(function RichTextEditor(
  { placeholder = "Escribe el mensaje…", onChange, testid = "rich-editor", minHeight = 200, className = "", initialText = "", initialHtml = null },
  ref
) {
  const editorRef = useRef(null);

  const emit = () => onChange?.({
    html: editorRef.current?.innerHTML || "",
    text: editorRef.current?.innerText || "",
  });

  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = initialHtml != null ? initialHtml : textToHtml(initialText);
    emit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    setHTML: (html) => { if (editorRef.current) { editorRef.current.innerHTML = html || ""; emit(); } },
    setText: (text) => { if (editorRef.current) { editorRef.current.innerHTML = textToHtml(text); emit(); } },
    getHTML: () => (editorRef.current?.innerText || "").trim() ? (editorRef.current?.innerHTML || "") : "",
    getText: () => (editorRef.current?.innerText || "").trim(),
    clear: () => { if (editorRef.current) { editorRef.current.innerHTML = ""; emit(); } },
    focus: () => editorRef.current?.focus(),
  }));

  const exec = (cmd) => { editorRef.current?.focus(); document.execCommand(cmd, false, null); emit(); };
  const addLink = () => {
    const url = window.prompt("URL del enlace (ej. tu YouCanBookMe):", "https://");
    if (url) { editorRef.current?.focus(); document.execCommand("createLink", false, url); emit(); }
  };

  return (
    <div className={`rounded-lg border ${className}`} data-testid={`${testid}-wrapper`}>
      <div className="flex flex-wrap items-center gap-0.5 border-b px-1.5 py-1">
        <ToolbarButton title="Negrita" onClick={() => exec("bold")} testid={`${testid}-bold`}><Bold className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Cursiva" onClick={() => exec("italic")} testid={`${testid}-italic`}><Italic className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Subrayado" onClick={() => exec("underline")} testid={`${testid}-underline`}><Underline className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Enlace / hipervínculo" onClick={addLink} testid={`${testid}-link`}><LinkIcon className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Lista" onClick={() => exec("insertUnorderedList")} testid={`${testid}-ul`}><List className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Lista numerada" onClick={() => exec("insertOrderedList")} testid={`${testid}-ol`}><ListOrdered className="h-4 w-4" /></ToolbarButton>
      </div>
      <div
        ref={editorRef}
        contentEditable
        role="textbox"
        aria-multiline="true"
        data-testid={testid}
        data-placeholder={placeholder}
        onInput={emit}
        style={{ minHeight }}
        className="prose-editor thin-scroll max-h-[40vh] overflow-y-auto px-3 py-2 text-sm leading-relaxed focus:outline-none"
        suppressContentEditableWarning
      />
    </div>
  );
});

export default RichTextEditor;
